import { Fragment } from 'react';
import { type Stage } from './helpers';
import { pipelineLanes, type LaneDef, type PipelineControlPlane } from './pipelineLayout';
import StageNode from './StageNode';
import QueueBadge from './QueueBadge';

function Lane({
  lane,
  stageMap,
  avgRates,
}: {
  lane: LaneDef;
  stageMap: Map<string, Stage>;
  avgRates: Map<string, number>;
}) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      <div className="w-28 shrink-0 text-right pr-2 text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
        {lane.label}
      </div>
      {lane.stages.map((def, idx) =>
        lane.offPipeline ? (
          // Independent queues, not a chain — each gets its own top-down input badge
          <div key={def.id} className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1 text-gray-400 dark:text-zinc-500">
              <span>↓</span>
              <QueueBadge depth={stageMap.get(def.id)?.queueDepth ?? 0} />
            </div>
            <StageNode def={def} stage={stageMap.get(def.id)} avgRatePerMin={avgRates.get(def.id)} />
          </div>
        ) : (
          <Fragment key={def.id}>
            {idx > 0 && (
              <div className="flex items-center gap-1 text-gray-400 dark:text-zinc-500">
                <QueueBadge depth={stageMap.get(def.id)?.queueDepth ?? 0} />
                <span>→</span>
              </div>
            )}
            <StageNode def={def} stage={stageMap.get(def.id)} avgRatePerMin={avgRates.get(def.id)} />
          </Fragment>
        ),
      )}
    </div>
  );
}

export default function PipelineLanes({
  stages,
  avgRates,
  controlPlane,
}: {
  stages: Stage[];
  avgRates: Map<string, number>;
  controlPlane?: PipelineControlPlane;
}) {
  const lanes = pipelineLanes(controlPlane);
  const stageMap = new Map(stages.map((s) => [s.id, s]));
  const onPipeline = lanes.filter((lane) => !lane.offPipeline);
  const offPipeline = lanes.filter((lane) => lane.offPipeline);
  return (
    <div className="flex flex-col gap-2">
      {onPipeline.map((lane) => (
        <Fragment key={lane.label}>
          {/* Every lane's input arrives from the top — including the intake
              lane, whose badge is the pipeline's entry queue */}
          <div className="pl-32 flex items-center gap-1 text-gray-400 dark:text-zinc-500">
            <span>↓</span>
            <QueueBadge depth={stageMap.get(lane.stages[0].id)?.queueDepth ?? 0} />
          </div>
          <Lane lane={lane} stageMap={stageMap} avgRates={avgRates} />
        </Fragment>
      ))}
      {offPipeline.map((lane) => (
        <div key={lane.label} className="mt-3 border-t border-dashed border-gray-200 dark:border-zinc-700 pt-3">
          <Lane lane={lane} stageMap={stageMap} avgRates={avgRates} />
        </div>
      ))}
    </div>
  );
}
